import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Clock, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function RecurringBills() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [newProfile, setNewProfile] = useState({
    vendor_id: '', profile_name: '', frequency: 'monthly',
    start_date: new Date().toISOString().split('T')[0], end_date: '',
    items: [{ description: '', quantity: 1, unit_price: 0 }], notes: ''
  });

  useEffect(() => { fetchData(); }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [profRes, vendRes] = await Promise.all([
        fetch(`${API}/api/purchases/recurring-bills`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/api/purchases/vendors`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (profRes.ok) setProfiles((await profRes.json()).profiles || []);
      if (vendRes.ok) setVendors((await vendRes.json()).vendors || []);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const createProfile = async () => {
    if (!newProfile.vendor_id || !newProfile.profile_name || !newProfile.items[0].unit_price) { toast.error('Fill required fields'); return; }
    setProcessing(true);
    try {
      const res = await fetch(`${API}/api/purchases/recurring-bills`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newProfile)
      });
      if (res.ok) { toast.success('Created'); setShowNew(false); fetchData(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (error) { toast.error('Failed'); }
    finally { setProcessing(false); }
  };

  const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amt || 0);

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="h-6 w-6 animate-spin" /></div>;

  return (
    <Card className="mt-4">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2"><Clock className="h-5 w-5" />Recurring Bills</CardTitle>
          <Dialog open={showNew} onOpenChange={setShowNew}>
            <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />New Profile</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Create Recurring Bill</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div><Label>Profile Name *</Label><Input value={newProfile.profile_name} onChange={(e) => setNewProfile({ ...newProfile, profile_name: e.target.value })} placeholder="e.g., Monthly Internet Bill" /></div>
                <div><Label>Vendor *</Label><Select value={newProfile.vendor_id} onValueChange={(v) => setNewProfile({ ...newProfile, vendor_id: v })}><SelectTrigger><SelectValue placeholder="Select vendor" /></SelectTrigger><SelectContent>{vendors.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.company_name}</SelectItem>)}</SelectContent></Select></div>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label>Frequency</Label><Select value={newProfile.frequency} onValueChange={(v) => setNewProfile({ ...newProfile, frequency: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="quarterly">Quarterly</SelectItem><SelectItem value="yearly">Yearly</SelectItem></SelectContent></Select></div>
                  <div><Label>Start Date</Label><Input type="date" value={newProfile.start_date} onChange={(e) => setNewProfile({ ...newProfile, start_date: e.target.value })} /></div>
                  <div><Label>End Date</Label><Input type="date" value={newProfile.end_date} onChange={(e) => setNewProfile({ ...newProfile, end_date: e.target.value })} /></div>
                </div>
                <div className="border rounded p-3 bg-slate-50">
                  <Label className="text-sm font-medium">Line Item</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div><Label className="text-xs">Description</Label><Input value={newProfile.items[0].description} onChange={(e) => setNewProfile({ ...newProfile, items: [{ ...newProfile.items[0], description: e.target.value }] })} /></div>
                    <div><Label className="text-xs">Amount *</Label><Input type="number" min="0" value={newProfile.items[0].unit_price} onChange={(e) => setNewProfile({ ...newProfile, items: [{ ...newProfile.items[0], unit_price: parseFloat(e.target.value) || 0 }] })} /></div>
                  </div>
                </div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button><Button onClick={createProfile} disabled={processing}>{processing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow><TableHead>Profile</TableHead><TableHead>Vendor</TableHead><TableHead>Frequency</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Next Bill</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {profiles.map((p) => (
              <TableRow key={p.profile_id}>
                <TableCell className="font-medium">{p.profile_name}</TableCell>
                <TableCell>{p.vendor_name}</TableCell>
                <TableCell className="capitalize">{p.frequency}</TableCell>
                <TableCell className="text-right">{formatCurrency(p.total_amount)}</TableCell>
                <TableCell>{p.next_bill_date}</TableCell>
                <TableCell><Badge variant={p.status === 'active' ? 'default' : 'secondary'}>{p.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {profiles.length === 0 && <div className="text-center py-8 text-slate-500">No recurring bills</div>}
      </CardContent>
    </Card>
  );
}
